///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// RTK Query endpoints for placemarks sync (/api/user/placemarks).
/// These are driven by the sync orchestrator (placemarksSync.ts) and the
/// live-push store listener rather than component hooks: the local slice
/// is the source of truth and works fully offline/signed-out.
///

import type { PlacemarksDelta, PutPlacemarkRequest } from "@shared-types"
import { authenticatedApi } from "./API/authenticatedApi"
import type { Placemark } from "./placemarksSlice"

/** Convert a client placemark to the PUT request body (camelCase → wire) */
export function placemarkToPutRequest(placemark: Placemark): PutPlacemarkRequest {
	return {
		name: placemark.name,
		locations: placemark.locations,
		anchor_id: placemark.anchorId ?? null,
		color: placemark.color,
		note: placemark.note ?? null,
		tags: placemark.tags,
		created_at: placemark.createdAt,
		updated_at: placemark.updatedAt,
	}
}

export const placemarksApi = authenticatedApi.injectEndpoints({
	endpoints: (builder) => ({
		getPlacemarksDelta: builder.query<PlacemarksDelta, { since: string | null }>({
			query: ({ since }) => ({
				url: since
					? `/api/user/placemarks?since=${encodeURIComponent(since)}`
					: "/api/user/placemarks",
			}),
			providesTags: ["UserCollections"],
		}),
		putPlacemark: builder.mutation<{ success: boolean }, { id: string; body: PutPlacemarkRequest }>({
			query: ({ id, body }) => ({
				url: `/api/user/placemarks/${id}`,
				method: "PUT",
				body,
			}),
		}),
		deletePlacemark: builder.mutation<{ success: boolean }, string>({
			query: (id) => ({
				url: `/api/user/placemarks/${id}`,
				method: "DELETE",
			}),
		}),
	}),
})

export const {
	useGetPlacemarksDeltaQuery,
	usePutPlacemarkMutation,
	useDeletePlacemarkMutation,
} = placemarksApi
